import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import Navbar from '../Navbar';
import Footer from '../Footer';

const certificationNames = [
  'OEKO-TEX Standard 100',
  'Global Recycled Standard (GRS)',
  'OEKO-TEX Recycled',
  'FSC',
  'Recycled Claim Standard (RCS)',
  'PETA-Approved Vegan',
  'SEDEX SMETA'
];

const SustainabilityPage = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      hero: {
        title: 'Certified Materials.\nAudited Facilities.',
        subtitle: 'Recycled and organic alternatives available across woven labels, printed labels, hangtags and packaging. Seven active certifications covering material origin, chemical safety and social compliance.'
      },
      s01: {
        number: '01',
        title: 'Active certifications',
        lead: 'Seven independent certifications covering material sourcing, chemical safety, animal welfare and social compliance. Audit reports available on request.',
        rows: [
          { cert: 'OEKO-TEX Standard 100', scope: 'Tested for harmful substances across all standard materials' },
          { cert: 'OEKO-TEX Recycled', scope: 'Verified recycled content in polyester yarns' },
          { cert: 'GRS — Global Recycled Standard', scope: 'Chain-of-custody for recycled materials' },
          { cert: 'RCS — Recycled Claim Standard', scope: 'Recycled content tracking, raw material to final product' },
          { cert: 'FSC — Forest Stewardship Council', scope: 'Responsibly sourced paper for hangtags and packaging (FSC-C183770)' },
          { cert: 'SEDEX SMETA', scope: 'Social compliance audit' },
          { cert: 'PETA-Approved Vegan', scope: 'No animal-derived materials in production' }
        ]
      },
      s02: {
        number: '02',
        title: 'Material options',
        lead: 'Sustainable alternatives available across every product line. Standard and certified materials run on the same equipment — no premium tier, no separate lead time.',
        columns: [
          {
            title: 'Yarns & threads',
            sustainable: ['Recycled polyester', 'Organic cotton'],
            standard: [],
            footer: ''
          },
          {
            title: 'Paper & board',
            sustainable: ['FSC-certified paper', 'Recycled paper'],
            standard: [],
            footer: ''
          },
          {
            title: 'Inks & coatings',
            sustainable: ['OEKO-TEX compliant inks', 'Water-based options'],
            standard: [],
            footer: ''
          }
        ]
      },
      s03: {
        number: '03',
        title: 'Production practices',
        lead: 'All practices below fall within the scope of our active certifications and are subject to third-party audit.',
        items: [
          { title: 'Chemical compliance', desc: 'All dyes, inks and coatings used in standard production are OEKO-TEX Standard 100 compliant. Substances on the OEKO-TEX restricted list are excluded from the supply chain.' },
          { title: 'Print options without solvents', desc: 'Water-based and solvent-free ink systems available across printed labels, hangtags and packaging. Selected per project based on substrate and finish.' },
          { title: 'Material traceability', desc: 'Recycled content tracked under GRS and RCS chain-of-custody. FSC-certified paper traced from forest to final product.' },
          { title: 'Audited facility operations', desc: 'All production facilities in Istanbul included in the SEDEX SMETA audit scope. Covers working conditions, health and safety, and environmental management.' }
        ]
      },
      s04: {
        number: '04',
        title: 'Social compliance',
        text: 'Operating under the SEDEX SMETA audit scope, our Istanbul facility adheres strictly to global standards for labor, health & safety, environment, and business ethics. With a team of approximately 50 professionals, we manage our core manufacturing processes in-house to maintain maximum oversight over quality and compliance. The latest audit report is available upon request.'
      },
      s05: {
        number: '05',
        title: 'Documentation requests',
        text: 'Certificate copies, audit summaries and material declarations are issued directly through our compliance team. Most documentation is shared within two business days.',
        button: 'Request documentation'
      }
    },
    tr: {
      hero: {
        title: 'Sertifikalı Malzemeler.\nDenetlenmiş Tesisler.',
        subtitle: 'Dokuma etiketler, baskılı etiketler, karton etiketler ve ambalaj genelinde geri dönüştürülmüş ve organik alternatifler mevcuttur. Malzeme kökeni, kimyasal güvenlik ve sosyal uyumluluğu kapsayan yedi aktif sertifika.'
      },
      s01: {
        number: '01',
        title: 'Aktif sertifikalar',
        lead: 'Malzeme tedariki, kimyasal güvenlik, hayvan refahı ve sosyal uyumluluğu kapsayan yedi bağımsız sertifika. Denetim raporları talep üzerine sunulur.',
        rows: [
          { cert: 'OEKO-TEX Standard 100', scope: 'Tüm standart malzemelerde zararlı maddeler için test edilmiştir' },
          { cert: 'OEKO-TEX Recycled', scope: 'Polyester ipliklerde doğrulanmış geri dönüştürülmüş içerik' },
          { cert: 'GRS — Global Recycled Standard', scope: 'Geri dönüştürülmüş malzemeler için gözetim zinciri' },
          { cert: 'RCS — Recycled Claim Standard', scope: 'Hammaddeden nihai ürüne geri dönüştürülmüş içerik takibi' },
          { cert: 'FSC — Forest Stewardship Council', scope: 'Karton etiketler ve ambalaj için sorumlu kaynaklı kağıt (FSC-C183770)' },
          { cert: 'SEDEX SMETA', scope: 'Sosyal uyumluluk denetimi' },
          { cert: 'PETA-Approved Vegan', scope: 'Üretimde hayvansal kaynaklı malzeme kullanılmamaktadır' }
        ]
      },
      s02: {
        number: '02',
        title: 'Malzeme seçenekleri',
        lead: 'Her ürün hattında sürdürülebilir alternatifler mevcuttur. Standart ve sertifikalı malzemeler aynı ekipman üzerinde çalışır — ayrı fiyat katmanı yok, ayrı teslim süresi yok.',
        columns: [
          {
            title: 'İplikler',
            sustainable: ['Geri dönüştürülmüş polyester', 'Organik pamuk'],
            standard: [],
            footer: ''
          },
          {
            title: 'Kağıt & karton',
            sustainable: ['FSC sertifikalı kağıt', 'Geri dönüştürülmüş kağıt'],
            standard: [],
            footer: ''
          },
          {
            title: 'Mürekkepler & kaplamalar',
            sustainable: ['OEKO-TEX uyumlu mürekkepler', 'Su bazlı seçenekler'],
            standard: [],
            footer: ''
          }
        ]
      },
      s03: {
        number: '03',
        title: 'Üretim uygulamaları',
        lead: 'Aşağıdaki tüm uygulamalar aktif sertifikalarımız kapsamındadır ve üçüncü taraf denetimine tabidir.',
        items: [
          { title: 'Kimyasal uyumluluk', desc: 'Standart üretimde kullanılan tüm boyalar, mürekkepler ve kaplamalar OEKO-TEX Standard 100 uyumludur. OEKO-TEX kısıtlı listesindeki maddeler tedarik zincirinden çıkarılmıştır.' },
          { title: 'Solventsiz baskı seçenekleri', desc: 'Baskılı etiketler, karton etiketler ve ambalajda su bazlı ve solventsiz mürekkep sistemleri mevcuttur. Substrat ve son işleme göre proje bazında seçilir.' },
          { title: 'Malzeme izlenebilirliği', desc: 'Geri dönüştürülmüş içerik GRS ve RCS gözetim zinciri altında takip edilir. FSC sertifikalı kağıt ormandan nihai ürüne kadar izlenir.' },
          { title: 'Denetlenmiş tesis operasyonları', desc: 'İstanbul\'daki dört tesisin tamamı SEDEX SMETA denetim kapsamındadır. Çalışma koşulları, iş sağlığı ve güvenliği ile çevre yönetimini kapsar.' }
        ]
      },
      s04: {
        number: '04',
        title: 'Sosyal uyumluluk',
        text: 'İstanbul\'daki Etiteks üretim tesislerinin tamamı, iş standartları, iş sağlığı ve güvenliği, çevre yönetimi ve iş etiğini kapsayan SEDEX SMETA denetim kapsamındadır. Üretim yaklaşık 50 çalışan ile bünyemizde gerçekleştirilmektedir, taşeron üretim yok, üçüncü taraf son işleme yok. En güncel denetim raporu talep üzerine müşterilere sunulur.'
      },
      s05: {
        number: '05',
        title: 'Dokümantasyon talepleri',
        text: 'Sertifika kopyaları, denetim özetleri ve malzeme beyannameleri doğrudan uyumluluk ekibimiz tarafından düzenlenir. Belgelerin çoğu iki iş günü içinde paylaşılır.',
        button: 'Dokümantasyon talep edin'
      }
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-white" data-testid="sustainability-page">
      <Navbar />
      
      {/* Hero */}
      <section className="relative min-h-[350px] flex items-end bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1448375240586-882707db888b?w=1600&q=70&fm=webp)' }}>
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 pb-12 pt-32">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight whitespace-pre-line" data-testid="sustainability-title">
            {t.hero.title}
          </h1>
          <p className="text-lg text-white/80 max-w-3xl leading-relaxed">
            {t.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Certification Logos */}
      <section className="py-12 border-b border-gray-200 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-8 md:gap-10 items-start justify-items-center">
            {[
              'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/z6os3fp8_sdadadsdadas.png',
              'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rd43kxco_Global%20Recycled%20Standard%20%28GRS%29%20%E2%80%94%20Recycled%20content%20with%20social%20and%20environmental%20criteria.png',
              'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/b1kii4km_OEKO-TEX%C2%AE%20Recycled%20%E2%80%94%20Certified%20for%20recycled%20textile%20products.png',
              'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/uhrbtqt7_FSC%C2%AE%20%E2%80%94%20Responsibly%20sourced%20paper%20and%20board.png',
              'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/qz6kus10_Recycled%20Claim%20Standard%20%28RCS%29%20%E2%80%94%20Recycled%20content%20traceability.png',
              'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/rt486shs_PETA%20Approved%20Vegan%20%E2%80%94%20No%20animal-derived%20materials.png',
              'https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/91jzm374_SEDEX%20-%20SMETA%20%E2%80%94%20Audit-ready%20social%20compliance.png'
            ].map((img, i) => (
              <div key={i} className="h-24 w-24 flex items-center justify-center">
                <img src={img} alt={`${certificationNames[i]} certificate logo`} className="max-h-24 max-w-24 object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active certifications */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[auto_1fr] gap-x-12 gap-y-0 items-start">
            <span className="hidden">{t.s01.number}</span>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t.s01.title}</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">{t.s01.lead}</p>
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left" data-testid="certifications-table">
                  <thead>
                    <tr className="border-b-2 border-gray-900">
                      <th className="pb-3 pr-8 text-sm font-semibold text-gray-900 uppercase tracking-wider">
                        {language === 'tr' ? 'Sertifika' : 'Certification'}
                      </th>
                      <th className="pb-3 text-sm font-semibold text-gray-900 uppercase tracking-wider">
                        {language === 'tr' ? 'Kapsam' : 'Scope'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.s01.rows.map((row, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-3 pr-8 text-sm font-medium text-gray-900 whitespace-nowrap">{row.cert}</td>
                        <td className="py-3 text-sm text-gray-500">{row.scope}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 02 — Material options */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[auto_1fr] gap-x-12 gap-y-0 items-start">
            <span className="hidden">{t.s02.number}</span>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t.s02.title}</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">{t.s02.lead}</p>
              
              <div className="grid md:grid-cols-3 gap-8" data-testid="material-options-grid">
                {t.s02.columns.map((col, i) => (
                  <div key={i}>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">{col.title}</h3>
                    <ul className="space-y-2 mb-3">
                      {col.sustainable.map((item, j) => (
                        <li key={j} className="flex items-start text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 bg-gray-900 rounded-full mr-2.5 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-gray-200 my-3" />
                    <ul className="space-y-2 mb-4">
                      {col.standard.map((item, j) => (
                        <li key={j} className="flex items-start text-sm text-gray-400">
                          <span className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-2.5 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-gray-400 leading-relaxed">{col.footer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 03 — Production practices */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[auto_1fr] gap-x-12 gap-y-0 items-start">
            <span className="hidden">{t.s03.number}</span>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t.s03.title}</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">{t.s03.lead}</p>
              
              <div className="space-y-8" data-testid="production-practices">
                {t.s03.items.map((item, i) => (
                  <div key={i}>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 04 — Social compliance */}
      <section className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[auto_1fr] gap-x-12 gap-y-0 items-start">
            <span className="hidden">{t.s04.number}</span>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t.s04.title}</h2>
              <p className="text-gray-500 leading-relaxed" data-testid="social-compliance-text">{t.s04.text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 05 — Documentation requests (CTA) */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-[auto_1fr] gap-x-12 gap-y-0 items-start">
            <span className="hidden">{t.s05.number}</span>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t.s05.title}</h2>
              <p className="text-gray-500 mb-8 leading-relaxed">{t.s05.text}</p>
              <a 
                href="mailto:compliance@etiteks.com"
                className="inline-block px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded transition-colors"
                data-testid="request-docs-btn"
              >
                {t.s05.button}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SustainabilityPage;
